import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Modal,
  Grid,
  TextField,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import TPC_URL from "../../../utils/TPC_URL";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: var(--mui-palette-background-default);
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  background-color: var(--mui-palette-background-paper);
  box-shadow: var(--mui-shadows-2);
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--mui-palette-divider);
`;

const ToolbarLeft = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const ToolbarRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`;

const TableContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 24px;
`;

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

function Shift() {
  const { group_code, project_code } = useParams();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({
    shift_name: "",
    shift_description: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const fetchShifts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${TPC_URL}/api/shift/getAllShifts?group_code=${group_code}&project_code=${project_code}`
      );

      if (response.data && Array.isArray(response.data)) {
        setTableData(response.data);
      } else {
        setTableData([]);
        setSnackbar({
          open: true,
          message: "데이터 형식이 유효하지 않습니다",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching shifts:", error);
      setSnackbar({
        open: true,
        message: "Shift 데이터 로딩 실패",
        severity: "error",
      });
      setTableData([]);
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code]);
  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);
  const resetForm = () => {
    setFormData({
      shift_name: "",
      shift_description: "",
    });
    setEditingRow(null);
  };
  const openCreateModal = () => {
    resetForm();
    setModalMode("create");
    setModalOpen(true);
  };
  const openEditModal = (row) => {
    setFormData({
      shift_name: row.shift_name || "",
      shift_description: row.shift_description || "",
    });
    setEditingRow(row);
    setModalMode("edit");
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };
  const handleCreateShift = async () => {
    try {
      await axios.post(`${TPC_URL}/api/shift/postShifts`, {
        group_code,
        project_code,
        info: {
          shift_name: formData.shift_name,
          shift_description: formData.shift_description,
        },
      });
      await fetchShifts();
      closeModal();
      setSnackbar({
        open: true,
        message: "Shift가 추가되었습니다",
        severity: "success",
      });
    } catch (error) {
      console.error("Error creating shift:", error);
      setSnackbar({
        open: true,
        message: "Shift 추가 실패",
        severity: "error",
      });
    }
  };
  const handleUpdateShift = async () => {
    try {
      await axios.put(`${TPC_URL}/api/shift/putShifts`, {
        group_code,
        project_code,
        info: {
          shift_id: editingRow.shift_id,
          shift_name: formData.shift_name,
          shift_description: formData.shift_description,
        },
      });
      await fetchShifts();
      closeModal();
      setSnackbar({
        open: true,
        message: "Shift가 수정되었습니다",
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating shift:", error);
      setSnackbar({
        open: true,
        message: "Shift 수정 실패",
        severity: "error",
      });
    }
  };
  const handleDeleteShift = async (row) => {
    try {
      if (!window.confirm("이 Shift를 삭제하시겠습니까?")) {
        return;
      }
      await axios.delete(
        `${TPC_URL}/api/shift/deleteShifts?group_code=${group_code}&project_code=${project_code}&shift_id=${row.original.shift_id}`
      );
      await fetchShifts();
      setSnackbar({
        open: true,
        message: "Shift가 삭제되었습니다",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting shift:", error);
      setSnackbar({
        open: true,
        message: "Shift 삭제 실패",
        severity: "error",
      });
    }
  };
  const columns = useMemo(
    () => [
      {
        accessorKey: "shift_id",
        header: "Shift ID",
        enableEditing: false,
        size: 100,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        muiTableFooterCellProps: { align: "center" },
      },
      {
        accessorKey: "shift_name",
        header: "조업명",
        size: 200,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        muiTableFooterCellProps: { align: "center" },
      },
      {
        accessorKey: "shift_description",
        header: "조업 설명",
        size: 300,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        muiTableFooterCellProps: { align: "center" },
      },
    ],
    []
  );
  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableEditing: false,
    enableColumnActions: true,
    enableColumnFilters: true,
    enablePagination: true,
    enableSorting: true,
    enableBottomToolbar: true,
    enableTopToolbar: false,
    initialState: {
      columnVisibility: {
        shift_id: false,
      },
    },
    getRowId: (row) => String(row.shift_id),
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: "inherit",
      },
    }),
    muiTableContainerProps: {
      sx: { maxHeight: "calc(100vh - 280px)" },
    },
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "0.5rem" }}>
        <Tooltip title="수정">
          <IconButton onClick={() => openEditModal(row.original)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="삭제">
          <IconButton onClick={() => handleDeleteShift(row)} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    positionActionsColumn: "last",
  });

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <ToolbarLeft>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ fontSize: "22px !important", color: "#111" }}
            >
              조업 관리
            </Typography>
          </ToolbarLeft>
          <ToolbarRight>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
            >
              추가
            </Button>
          </ToolbarRight>
        </ToolbarContainer>
        <TableContainer>
          {loading && tableData.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          ) : (
            <MaterialReactTable table={table} />
          )}
        </TableContainer>
      </FrameContainer>
      <Modal
        open={modalOpen}
        onClose={closeModal}
        aria-labelledby="shift-modal-title"
      >
        <Box sx={modalStyle}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography id="shift-modal-title" variant="h6" component="h2">
              {modalMode === "create" ? "Shift 추가" : "Shift 수정"}
            </Typography>
            <IconButton onClick={closeModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Shift 이름"
                name="shift_name"
                value={formData.shift_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    shift_name: e.target.value,
                  }))
                }
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Shift 설명"
                name="shift_description"
                value={formData.shift_description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    shift_description: e.target.value,
                  }))
                }
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
            <Grid
              item
              xs={12}
              mt={1}
              display="flex"
              justifyContent="flex-end"
              gap={1}
            >
              <Button variant="outlined" onClick={closeModal}>
                취소
              </Button>
              <Button
                variant="contained"
                onClick={
                  modalMode === "create" ? handleCreateShift : handleUpdateShift
                }
                disabled={!formData.shift_name.trim()}
              >
                저장
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default Shift;
